'use client';
/**
 * LE POP-UP PROGRAMMÉ — la fenêtre qui accueille (P1, 26/08/2026).
 * ────────────────────────────────────────────────────────────────
 * « Un petit mot (les fêtes), une photo, ou un mini-sondage — l'humeur du
 * jour. » Programmé depuis le paramétrage, il s'affiche à l'OUVERTURE de
 * l'application, à toute personne connectée.
 *
 * ── Trois règles d'affichage ──────────────────────────────────────────────
 *
 *  1. **Une fois par personne.** La vue est accusée au serveur ; l'annonce ne
 *     revient plus — un mot de fête qui reviendrait à chaque écran serait un
 *     harcèlement, pas une attention.
 *  2. **Fermer sans répondre est un droit.** Le sondage est facultatif : la
 *     croix accuse la vue et n'enregistre aucune réponse. Un pop-up qu'on ne
 *     peut fermer qu'en votant fabrique des réponses de guerre lasse.
 *  3. **Répondre ferme.** Un clic, un merci, fini — l'humeur du jour ne mérite
 *     pas un deuxième écran.
 */
import React, { useEffect, useState } from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { card, COUCHES } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
interface Annonce {
  documentId: string;
  titre: string;
  corps: string;
  imageData: string | null;
  sondage: { question: string; options: string[] } | null;
}

export function AnnonceDuJour() {
  const booted = useGed((s) => s.booted);
  const [annonce, setAnnonce] = useState<Annonce | null>(null);
  const [merci, setMerci] = useState(false);

  useEffect(() => {
    if (!booted) return;
    let vivant = true;
    void fetch('/api/ged/ged/annonces/courante', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j?.annonce) setAnnonce(j.annonce); })
      /* Un échec de lecture n'affiche RIEN : le pop-up est une attention, pas
         une fonction — son absence ne se signale pas. */
      .catch(() => undefined);
    return () => { vivant = false; };
  }, [booted]);

  if (!annonce) return null;

  const accuser = (reponse?: string) => {
    /* L'accusé part, la fenêtre se ferme sans attendre : le serveur tient
       l'unicité, et bloquer la fermeture sur le réseau ferait payer à l'humeur
       du jour le prix d'une transaction. */
    void fetchEcriture(`/api/ged/ged/annonces/${annonce.documentId}/vue`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reponse ? { reponse } : {}),
    }).catch(() => undefined);
    if (reponse) {
      setMerci(true);
      setTimeout(() => setAnnonce(null), 900);
    } else {
      setAnnonce(null);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={annonce.titre}
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.TRAVAIL, background: 'rgba(0,0,0,.4)', display: 'grid', placeItems: 'center', padding: 20 }}
      /* Fenêtre de CONSULTATION : le clic à côté referme (confort gardé le
         04/09/2026). ⚠️ Ici il ACQUITTE aussi — c'est le sens même de
         l'annonce : la refermer, c'est l'avoir vue. */
      onClick={() => accuser()}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...card, padding: 22, maxWidth: 460, width: '100%', display: 'grid', gap: 12, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 17, color: 'var(--text-strong)' }}>{annonce.titre}</h2>
        {annonce.imageData && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={annonce.imageData.startsWith('data:') ? annonce.imageData : `data:image/jpeg;base64,${annonce.imageData}`}
            alt="" style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 'var(--radius-md)', justifySelf: 'center' }} />
        )}
        {annonce.corps && (
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{annonce.corps}</p>
        )}
        {annonce.sondage && !merci && (
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{annonce.sondage.question}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {annonce.sondage.options.map((o) => (
                <Bouton taille="sm" key={o} type="button" onClick={() => accuser(o)}>{o}</Bouton>
              ))}
            </div>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)' }}>
              Les réponses sont comptées sans être nommées.
            </p>
          </div>
        )}
        {merci && <p style={{ margin: 0, fontSize: 13, color: 'var(--green-600)', fontWeight: 600 }}>Merci !</p>}
        {!merci && (
          <Bouton taille="sm" type="button" onClick={() => accuser()} style={{ justifySelf: 'center' }}>
            Fermer
          </Bouton>
        )}
      </div>
    </div>
  );
}
